import type { ActivityCockpitSnapshot } from '@internal/spassu-code-contract';
import type { CockpitMetricView } from './cockpitViewModel';
import { formatDurationMs } from './formatters';
import type { MetricInput } from './metricInput';

export function leadTimeMetric(
  snapshot: ActivityCockpitSnapshot,
  metric: (input: MetricInput) => CockpitMetricView,
): CockpitMetricView {
  const section = snapshot.sections.time;
  const rawValue = section.value?.leadTimeMs ?? null;
  return metric({
    id: 'lead-time',
    label: 'Tempo total observado',
    rawValue,
    displayValue: formatDurationMs(rawValue),
    // formatDurationMs already carries the unit; a second one only repeats it
    unit: null,
    status: section.status,
    coverage: section.coverage,
    provenance: section.provenance,
    explanation: 'Do primeiro evento elegível até a última observação.',
    missingReason: 'A janela ainda não possui início e fim elegíveis.',
    relation: 'Representa a janela que contém os demais tempos observados.',
    limitations: section.limitations,
    technicalValue: rawValue === null ? null : `${rawValue} ms`,
    primary: true,
    updatedAt: section.updatedAt,
    sourceCutoffAt: snapshot.sourceCutoffAt,
    ...timeContext(snapshot, false),
    proportion: timeProportion(snapshot, false),
    reasonCode: rawValue === null ? 'time-window-unavailable' : null,
  });
}

export function unclassifiedTimeMetric(
  snapshot: ActivityCockpitSnapshot,
  metric: (input: MetricInput) => CockpitMetricView,
): CockpitMetricView {
  const section = snapshot.sections.time;
  const rawValue = section.value?.totalsMs.unclassified ?? null;
  return metric({
    id: 'unclassified-time',
    label: 'Tempo sem classificação',
    rawValue,
    displayValue: formatDurationMs(rawValue),
    // formatDurationMs already carries the unit; a second one only repeats it
    unit: null,
    status: section.status,
    coverage: section.coverage,
    provenance: section.provenance,
    explanation: 'Intervalos sem evidência suficiente para classificação.',
    missingReason: 'Não há projeção temporal disponível.',
    relation:
      'Faz parte do tempo total observado e não presume tempo de espera.',
    limitations: section.limitations,
    technicalValue: rawValue === null ? null : `${rawValue} ms`,
    primary: true,
    updatedAt: section.updatedAt,
    sourceCutoffAt: snapshot.sourceCutoffAt,
    ...timeContext(snapshot, true),
    proportion: timeProportion(snapshot, true),
    reasonCode: rawValue === null ? 'time-window-unavailable' : null,
  });
}

function timeProportion(
  snapshot: ActivityCockpitSnapshot,
  unclassified: boolean,
): string | null {
  const time = snapshot.sections.time.value;
  if (!time || time.leadTimeMs === null || time.leadTimeMs === 0) return null;
  const unclassifiedMs = Math.min(
    Math.max(time.totalsMs.unclassified, 0),
    time.leadTimeMs,
  );
  const ratio = unclassified
    ? unclassifiedMs / time.leadTimeMs
    : (time.leadTimeMs - unclassifiedMs) / time.leadTimeMs;
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(ratio);
  return unclassified
    ? `${formatted} do tempo observado permanece sem classificação.`
    : `${formatted} do tempo observado possui classificação.`;
}

function timeContext(snapshot: ActivityCockpitSnapshot, unclassified: boolean) {
  const intervals = (snapshot.sections.time.value?.intervals ?? []).filter(
    interval => !unclassified || interval.category === 'unclassified',
  );
  return {
    periodStartedAt: intervals[0]?.startedAt ?? null,
    periodEndedAt: intervals.at(-1)?.endedAt ?? null,
    provisional: snapshot.sections.time.value?.provisional ?? false,
  };
}
